import { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Rocket, Clock, Bell, Target, 
  Brain, Trophy, Beaker
} from 'lucide-react';
import { trackPageView } from '../utils/trackingUtils';
import WaitlistSignUpButton from '../components/buttons/WaitlistSignUpButton';

const upcomingFeatures = [
  {
    icon: <Beaker className="w-8 h-8 text-yellow-300" />,
    title: "The Lab",
    description: "Where raw talent is tested. Learn fast and challenge yourself"
  },
  {
    icon: <Target className="w-8 h-8 text-yellow-300" />,
    title: "The Pendulum",
    description: "Theory meets reality. Adapt, apply, and prove yourself"
  },
  {
    icon: <Brain className="w-8 h-8 text-yellow-300" />,
    title: "The Capstone",
    description: "No more safety nets. Develop and launch something real"
  },
  {
    icon: <Trophy className="w-8 h-8 text-yellow-300" />,
    title: "The Venture",
    description: "This is it. Take what you've built and make it your own"
  }
];

export default function VBCLearningPath() {
  useEffect(() => {
    window.scrollTo(0, 0);
    trackPageView('VBC_LEARNING_PATH', 'vbc-learning-path');
  }, []);

  return (
    <>
      <Helmet>
        <title>VBC Venture - Coming Soon | EmploSkills</title>
        <meta 
          name="description" 
          content="Ideas are easy, but execution is everything. Join the VBC Learning Path and prove you have what it takes."
        />
      </Helmet>

      <section className="relative bg-gradient-to-r from-primary to-blue-600 text-white min-h-screen flex items-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="text-center">
            <div className="flex justify-center mb-6 opacity-0 animate-fade-in">
              <span className="bg-yellow-400/20 text-yellow-300 px-4 py-2 rounded-full font-medium inline-flex items-center gap-2">
                <Clock className="w-4 h-4" />
                Coming Soon
              </span>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold mb-6 opacity-0 animate-fade-in">
              Are You Ready to Take the Next Step?
            </h1>
            <p className="text-xl md:text-2xl mb-6 max-w-3xl mx-auto font-medium opacity-0 animate-fade-in delay-200">
              Ideas are easy, but execution is everything.
            </p>
            <p className="text-3xl font-bold mb-12 text-yellow-300 opacity-0 animate-fade-in delay-300">
              Will You Make It?
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto mb-16 opacity-0 animate-fade-in delay-300">
              {upcomingFeatures.map((feature) => (
                <div 
                  key={feature.title}
                  className="bg-white/10 backdrop-blur-sm rounded-xl p-6 text-center hover:bg-white/20 transition-all duration-300"
                >
                  <div className="mb-4 flex justify-center">
                    {feature.icon}
                  </div>
                  <h3 className="text-xl font-semibold mb-2">{feature.title}</h3>
                  <p className="text-blue-100">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 max-w-2xl mx-auto mb-12 opacity-0 animate-fade-in delay-300">
              <Rocket className="w-12 h-12 mx-auto mb-6 text-yellow-300" />
              <h2 className="text-2xl font-bold mb-4">Some Will Hesitate. Others Will Build.</h2>
              <p className="text-lg mb-6">
                Join the waitlist to be among the first to access this revolutionary learning experience.
              </p>
              <WaitlistSignUpButton variant="white" className="px-8 py-4 text-lg" />
            </div>

            <div className="flex items-center justify-center gap-2 text-yellow-300 opacity-0 animate-fade-in delay-400">
              <Bell className="w-5 h-5" />
              <span>Limited spots available for early access</span>
            </div>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white to-transparent" />
      </section>
    </>
  );
} 